import { ErrorOutline, HourglassEmpty } from '@mui/icons-material'
import { Box, Tooltip, Typography } from '@mui/material'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { BomItemType } from 'model/Project/BoMItemRow'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { bomItemSelector } from '../store/selectors/bomItemSelector'
import { projectInBuyerViewSelector } from '../store/selectors/projectInBuyerViewSelector'

export function ImnooEstimationStatus({
  pending,
  failed,
  canRetry,
  compact,
}: {
  pending?: boolean
  failed?: boolean
  canRetry?: boolean
  compact?: boolean
}) {
  if (!pending && !failed) return null
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.5,
      }}
    >
      {pending && <ImnooStatusIndicator compact={compact} />}
      {failed && (
        <ImnooStatusIndicator failed canRetry={canRetry} compact={compact} />
      )}
    </Box>
  )
}

function ImnooStatusIndicator({
  failed,
  canRetry,
  compact,
}: {
  failed?: boolean
  canRetry?: boolean
  compact?: boolean
}) {
  const { t } = useTranslation()
  const label = failed
    ? t('project:imnoo-estimate-failed', {
        defaultValue: 'Imnoo estimate failed',
      })
    : t('project:imnoo-analysis-pending', {
        defaultValue: 'Imnoo estimate in progress',
      })
  const hint = failed
    ? canRetry
      ? t('project:imnoo-retry-hint', {
          defaultValue:
            'Use “Retry failed Imnoo estimates” in the project menu, or enter a manual estimate.',
        })
      : t('project:imnoo-settings-hint', {
          defaultValue:
            'Check the part’s estimation settings and required inputs, or enter a complete manual estimate.',
        })
    : t('project:imnoo-pending-hint', {
        defaultValue:
          'The estimate will update automatically. You can continue working or enter a manual estimate.',
      })
  return (
    <Tooltip title={hint}>
      <Box
        component="span"
        role="status"
        aria-label={label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: failed ? 'error.main' : 'text.secondary',
        }}
      >
        {failed ? (
          <ErrorOutline fontSize="small" />
        ) : (
          <HourglassEmpty fontSize="small" />
        )}
        {!compact && (
          <Typography component="span" variant="caption">
            {label}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}

export function BomItemImnooStatus({
  bomItemPointer,
  compact,
}: {
  bomItemPointer: BomItemPointer
  compact?: boolean
}) {
  const row = useAppSelector((state) => bomItemSelector(state, bomItemPointer))
  const buyer = useAppSelector(projectInBuyerViewSelector)
  if (
    buyer ||
    (row?.type !== BomItemType.partType &&
      row?.type !== BomItemType.partInstance)
  )
    return null
  return (
    <ImnooEstimationStatus
      pending={row.isImnooEstimationPending}
      failed={row.hasFailedImnooEstimation}
      canRetry={row.canRetryImnooEstimation}
      compact={compact}
    />
  )
}
